import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { MapPin, Star, Filter, Search, Navigation, Calendar } from 'lucide-react';
import { Card } from '../components/ui/Card';
import { Badge } from '../components/ui/Badge';
import { ResponsiveContainer, Tooltip, BarChart, Bar, XAxis, YAxis, CartesianGrid } from 'recharts';
const regionalRatings = [{
  region: 'Deutschland',
  OBI: 4.3,
  Bauhaus: 4.1,
  Hornbach: 4.4,
  toom: 3.9,
  Hagebau: 4.0,
  markets: 350
}, {
  region: 'Österreich',
  OBI: 4.4,
  Bauhaus: 4.2,
  Hornbach: 4.5,
  toom: 0,
  Hagebau: 4.1,
  markets: 85
}, {
  region: 'Schweiz',
  OBI: 4.5,
  Bauhaus: 4.3,
  Hornbach: 4.6,
  toom: 0,
  Hagebau: 0,
  markets: 12
}, {
  region: 'Tschechien',
  OBI: 4.1,
  Bauhaus: 4.0,
  Hornbach: 4.2,
  toom: 0,
  Hagebau: 0,
  markets: 35
}, {
  region: 'Ungarn',
  OBI: 3.9,
  Bauhaus: 3.8,
  Hornbach: 0,
  toom: 0,
  Hagebau: 0,
  markets: 28
}, {
  region: 'Polen',
  OBI: 4.0,
  Bauhaus: 3.9,
  Hornbach: 0,
  toom: 0,
  Hagebau: 0,
  markets: 55
}, {
  region: 'Italien',
  OBI: 4.2,
  Bauhaus: 4.0,
  Hornbach: 0,
  toom: 0,
  Hagebau: 0,
  markets: 58
}];
const radiusData: Record<string, {
  name: string;
  type: string;
  rating: number;
  distance: string;
}[]> = {
  'Hamburg, Jungfernstieg': [{
    name: 'OBI Markt Hamburg-Altona',
    type: 'OBI',
    rating: 4.2,
    distance: '2.6 km'
  }, {
    name: 'Bauhaus Hamburg-Lokstedt',
    type: 'Bauhaus',
    rating: 4.1,
    distance: '4.2 km'
  }, {
    name: 'Hornbach Hamburg-Eidelstedt',
    type: 'Hornbach',
    rating: 4.4,
    distance: '5.8 km'
  }, {
    name: 'toom Baumarkt Hamburg',
    type: 'toom',
    rating: 3.8,
    distance: '3.1 km'
  }],
  'Berlin, Alexanderplatz': [{
    name: 'OBI Markt Berlin-Mitte',
    type: 'OBI',
    rating: 4.3,
    distance: '1.3 km'
  }, {
    name: 'Bauhaus Berlin-Wedding',
    type: 'Bauhaus',
    rating: 4.0,
    distance: '3.5 km'
  }, {
    name: 'Hornbach Berlin-Weißensee',
    type: 'Hornbach',
    rating: 4.5,
    distance: '4.7 km'
  }, {
    name: 'Hagebaumarkt Berlin',
    type: 'Hagebau',
    rating: 3.9,
    distance: '2.2 km'
  }],
  'München, Marienplatz': [{
    name: 'OBI Markt München-Freiham',
    type: 'OBI',
    rating: 4.1,
    distance: '8.9 km'
  }, {
    name: 'Bauhaus München-Landsberger',
    type: 'Bauhaus',
    rating: 4.2,
    distance: '5.4 km'
  }, {
    name: 'Hornbach München-Fröttmaning',
    type: 'Hornbach',
    rating: 4.6,
    distance: '9.3 km'
  }, {
    name: 'toom Baumarkt München',
    type: 'toom',
    rating: 3.9,
    distance: '4.1 km'
  }]
};
const regions = ['Alle Länder', 'Deutschland', 'Österreich', 'Schweiz', 'Tschechien', 'Ungarn', 'Polen', 'Italien'];
const radiusOptions = ['1 km', '2 km', '5 km', '10 km'];
const competitors = ['OBI', 'Bauhaus', 'Hornbach', 'toom', 'Hagebau'];
const competitorBarColors: Record<string, string> = {
  OBI: '#FF6600',
  Bauhaus: '#e0001d',
  Hornbach: '#f89314',
  toom: '#ba0010',
  Hagebau: '#009745'
};
const competitorLogos: Record<string, string> = {
  OBI: 'https://www.obi.de/assets/icons/logos/obi-logo-orange.svg',
  Bauhaus: 'https://upload.wikimedia.org/wikipedia/de/thumb/4/4b/Bauhaus_%28Baumarkt%29_logo.svg/330px-Bauhaus_%28Baumarkt%29_logo.svg.png',
  Hornbach: 'https://upload.wikimedia.org/wikipedia/commons/thumb/d/dc/Hornbach_Logo_black.svg/330px-Hornbach_Logo_black.svg.png',
  toom: 'https://upload.wikimedia.org/wikipedia/commons/thumb/4/44/Toom_Baumarkt.svg/330px-Toom_Baumarkt.svg.png',
  Hagebau: 'https://upload.wikimedia.org/wikipedia/commons/thumb/d/d3/Hagebau_Logo_2022.svg/330px-Hagebau_Logo_2022.svg.png'
};
const timePeriods = [{
  id: '30',
  label: '30 Tage'
}, {
  id: '60',
  label: '60 Tage'
}, {
  id: '120',
  label: '120 Tage'
}, {
  id: '365',
  label: '365 Tage'
}, {
  id: 'gesamt',
  label: 'Gesamt'
}];
function applyTimeFactor(value: number, period: string, isRating: boolean = false): number {
  if (period === 'gesamt' || value === 0) return value;
  if (isRating) {
    const noise = period === '30' ? 0.1 : period === '60' ? -0.1 : period === '120' ? 0.05 : -0.05;
    return Math.max(1, Math.min(5, value + noise));
  }
  const factors: Record<string, number> = {
    '30': 0.08,
    '60': 0.17,
    '120': 0.33,
    '365': 1.0
  };
  return Math.max(1, Math.round(value * (factors[period] || 1)));
}
export function CompetitorRadarPage() {
  const [selectedRegion, setSelectedRegion] = useState('Alle Länder');
  const [selectedPeriod, setSelectedPeriod] = useState('gesamt');
  const [addressInput, setAddressInput] = useState('');
  const [selectedRadius, setSelectedRadius] = useState('5 km');
  const [addressSearched, setAddressSearched] = useState('Hamburg, Jungfernstieg');
  const [hasSearched, setHasSearched] = useState(true);
  const rawFilteredRegionalData = selectedRegion === 'Alle Länder' ? regionalRatings : regionalRatings.filter(r => r.region === selectedRegion);
  const filteredRegionalData = rawFilteredRegionalData.map(r => ({
    ...r,
    OBI: applyTimeFactor(r.OBI, selectedPeriod, true),
    Bauhaus: applyTimeFactor(r.Bauhaus, selectedPeriod, true),
    Hornbach: r.Hornbach > 0 ? applyTimeFactor(r.Hornbach, selectedPeriod, true) : 0,
    toom: r.toom > 0 ? applyTimeFactor(r.toom, selectedPeriod, true) : 0,
    Hagebau: r.Hagebau > 0 ? applyTimeFactor(r.Hagebau, selectedPeriod, true) : 0,
    markets: applyTimeFactor(r.markets, selectedPeriod)
  }));
  const regionBarData = selectedRegion === 'Alle Länder' ? (() => {
    const avg = (key: string) => {
      const vals = filteredRegionalData.map(r => (r as any)[key]).filter((v: number) => v > 0);
      return vals.length > 0 ? vals.reduce((a: number, b: number) => a + b, 0) / vals.length : null;
    };
    return [{
      region: 'Gesamt',
      OBI: avg('OBI'),
      Bauhaus: avg('Bauhaus'),
      Hornbach: avg('Hornbach'),
      toom: avg('toom'),
      Hagebau: avg('Hagebau')
    }];
  })() : filteredRegionalData.map(r => ({
    region: r.region,
    OBI: r.OBI,
    Bauhaus: r.Bauhaus,
    Hornbach: r.Hornbach || null,
    toom: r.toom || null,
    Hagebau: r.Hagebau || null
  }));
  const renderBarLogo = (comp: string) => (props: any) => {
    const {
      x,
      y,
      width,
      height,
      value
    } = props;
    if (!value || value === 0 || width < 6) return null;
    const logoW = Math.max(width + 8, 40);
    const logoH = 28;
    return <image href={competitorLogos[comp]} x={x + (width - logoW) / 2} y={y - logoH - 4} width={logoW} height={logoH} preserveAspectRatio="xMidYMid meet" data-id="element-321" />;
  };
  const rawRadiusStores = radiusData[addressSearched] || radiusData['Hamburg, Jungfernstieg'];
  const radiusStores = rawRadiusStores.map(s => ({
    ...s,
    rating: applyTimeFactor(s.rating, selectedPeriod, true)
  }));
  const radiusAggregated = competitors.map(type => {
    const stores = radiusStores.filter(s => s.type === type);
    const avg = stores.length > 0 ? stores.reduce((acc, s) => acc + s.rating, 0) / stores.length : null;
    return {
      name: type,
      rating: avg,
      count: stores.length
    };
  }).filter(d => d.rating !== null);
  const handleSearch = () => {
    const key = Object.keys(radiusData).find(k => k.toLowerCase().includes(addressInput.toLowerCase().split(',')[0].toLowerCase())) || 'Hamburg, Jungfernstieg';
    setAddressSearched(key);
    setHasSearched(true);
  };
  return <div className="p-6 space-y-6 overflow-y-auto h-[calc(100vh-64px)]" data-id="element-322">
      <Card data-id="element-323">
        <div className="p-6" data-id="element-324">
          <div className="flex items-start justify-between mb-5 flex-wrap gap-4" data-id="element-325">
            <div data-id="element-326">
              <h3 className="text-lg font-semibold text-gray-900" data-id="element-327">
                Regionaler Bewertungsvergleich
              </h3>
              <p className="text-sm text-gray-500 mt-0.5" data-id="element-328">
                Durchschnittliche Google-Bewertungen nach Land
              </p>
            </div>
          </div>

          <div className="flex flex-col gap-4 mb-5" data-id="element-329">
            <div className="flex items-center gap-3 flex-wrap" data-id="element-330">
              <div className="flex items-center gap-2 text-sm font-medium text-gray-600" data-id="element-331">
                <Filter size={14} data-id="element-332" />
                <span data-id="element-333">Land:</span>
              </div>
              <div className="flex flex-wrap gap-2" data-id="element-334">
                {regions.map(region => <button key={region} onClick={() => setSelectedRegion(region)} className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${selectedRegion === region ? 'bg-obi-orange text-white shadow-sm' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`} data-id="element-335">
                    {region}
                  </button>)}
              </div>
            </div>

            <div className="flex items-center gap-3 flex-wrap" data-id="element-336">
              <div className="flex items-center gap-2 text-sm font-medium text-gray-600" data-id="element-337">
                <Calendar size={14} data-id="element-338" />
                <span data-id="element-339">Zeitraum:</span>
              </div>
              <div className="flex flex-wrap gap-2" data-id="element-340">
                {timePeriods.map(period => <button key={period.id} onClick={() => setSelectedPeriod(period.id)} className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${selectedPeriod === period.id ? 'bg-obi-orange text-white shadow-sm' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`} data-id="element-341">
                    {period.label}
                  </button>)}
              </div>
            </div>
          </div>

          <div className="h-96" data-id="element-342">
            <ResponsiveContainer width="100%" height="100%" data-id="element-343">
              <BarChart data={regionBarData} margin={{
              top: 40,
              right: 20,
              left: 0,
              bottom: 5
            }} barCategoryGap={selectedRegion === 'Alle Länder' ? '15%' : '12%'} barGap={selectedRegion === 'Alle Länder' ? 4 : 2} data-id="element-344">
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} data-id="element-345" />
                <XAxis dataKey="region" axisLine={false} tickLine={false} tick={{
                fontSize: 11,
                fill: '#6b7280'
              }} data-id="element-346" />
                <YAxis domain={[3, 5]} axisLine={false} tickLine={false} tick={{
                fontSize: 11,
                fill: '#9ca3af'
              }} tickFormatter={v => v.toFixed(1) + ' ★'} data-id="element-347" />
                <Tooltip contentStyle={{
                borderRadius: '8px',
                border: 'none',
                boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                fontSize: '12px'
              }} formatter={(value: number, name: string) => [value.toFixed(1) + ' ★', name]} data-id="element-348" />
                {competitors.map(comp => <Bar key={comp} dataKey={comp} name={comp} fill={competitorBarColors[comp]} radius={[3, 3, 0, 0]} fillOpacity={comp === 'OBI' ? 1 : 0.55} label={renderBarLogo(comp)} data-id="element-349" />)}
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </Card>

      <Card data-id="element-350">
        <div className="p-6" data-id="element-351">
          <div className="flex items-start justify-between mb-5 flex-wrap gap-4" data-id="element-352">
            <div data-id="element-353">
              <h3 className="text-lg font-semibold text-gray-900" data-id="element-354">
                Lokale Umkreissuche
              </h3>
              <p className="text-sm text-gray-500 mt-0.5" data-id="element-355">
                Bewertungen im direkten Umfeld vergleichen
              </p>
            </div>
          </div>

          <div className="flex gap-3 mb-5 flex-wrap" data-id="element-356">
            <div className="flex-1 min-w-48 relative" data-id="element-357">
              <MapPin size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" data-id="element-358" />
              <input type="text" placeholder="Adresse eingeben, z.B. Hamburg, Jungfernstieg" value={addressInput} onChange={e => setAddressInput(e.target.value)} onKeyDown={e => e.key === 'Enter' && handleSearch()} className="w-full pl-9 pr-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-obi-orange/20 focus:border-obi-orange" data-id="element-359" />
            </div>
            <div className="flex items-center gap-2" data-id="element-360">
              <span className="text-sm text-gray-500 whitespace-nowrap" data-id="element-361">
                Radius:
              </span>
              <div className="flex gap-1" data-id="element-362">
                {radiusOptions.map(r => <button key={r} onClick={() => setSelectedRadius(r)} className={`px-3 py-2 rounded-lg text-xs font-medium transition-all ${selectedRadius === r ? 'bg-obi-orange text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`} data-id="element-363">
                    {r}
                  </button>)}
              </div>
            </div>
            <button onClick={handleSearch} className="flex items-center gap-2 px-4 py-2.5 bg-obi-orange text-white rounded-lg text-sm font-medium hover:bg-obi-dark transition-colors" data-id="element-364">
              <Search size={14} data-id="element-365" /> Suchen
            </button>
          </div>

          {hasSearched && <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} transition={{
          duration: 0.2
        }} data-id="element-366">
              <div className="text-xs text-gray-500 mb-4 flex items-center gap-1.5" data-id="element-367">
                <Navigation size={12} data-id="element-368" />
                <span data-id="element-369">
                  Ergebnisse für{' '}
                  <strong className="text-gray-700" data-id="element-370">{addressSearched}</strong>{' '}
                  im Umkreis von{' '}
                  <strong className="text-gray-700" data-id="element-371">{selectedRadius}</strong> —{' '}
                  {radiusStores.length} Märkte gefunden
                </span>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-5" data-id="element-372">
                <div data-id="element-373">
                  <div className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-3" data-id="element-374">
                    Ø Bewertung nach Kette
                  </div>
                  <div className="space-y-3" data-id="element-375">
                    {radiusAggregated.sort((a, b) => (b.rating || 0) - (a.rating || 0)).map((item, i) => <div key={item.name} className="flex items-center gap-3" data-id="element-376">
                          <img src={competitorLogos[item.name]} alt={item.name} className="h-4 w-auto max-w-[60px] object-contain shrink-0" data-id="element-377" />
                          <div className="w-20 text-sm font-medium text-gray-700" data-id="element-378">
                            {item.name}
                          </div>
                          <div className="flex-1 h-7 bg-gray-100 rounded-full overflow-hidden relative" data-id="element-379">
                            <motion.div initial={{
                      width: 0
                    }} animate={{
                      width: `${(item.rating || 0) / 5 * 100}%`
                    }} transition={{
                      duration: 0.5,
                      delay: i * 0.08
                    }} className="h-full rounded-full" style={{
                      backgroundColor: competitorBarColors[item.name],
                      opacity: item.name === 'OBI' ? 1 : 0.6
                    }} data-id="element-380" />
                          </div>
                          <div className="flex items-center gap-1 w-16 text-right" data-id="element-381">
                            <span className={`text-sm font-bold ${item.name === 'OBI' ? 'text-obi-orange' : 'text-gray-600'}`} data-id="element-382">
                              {item.rating?.toFixed(1)}
                            </span>
                            <Star size={11} className="text-yellow-400 fill-yellow-400" data-id="element-383" />
                          </div>
                          <div className="text-xs text-gray-400 w-14" data-id="element-384">
                            {item.count} Märkte
                          </div>
                        </div>)}
                  </div>
                </div>

                <div data-id="element-385">
                  <div className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-3" data-id="element-386">
                    Einzelne Märkte im Umkreis
                  </div>
                  <div className="space-y-2" data-id="element-387">
                    {radiusStores.map((store, i) => <motion.div key={i} initial={{
                  opacity: 0,
                  x: 10
                }} animate={{
                  opacity: 1,
                  x: 0
                }} transition={{
                  delay: i * 0.06
                }} className={`flex items-center justify-between p-2.5 rounded-lg border ${store.type === 'OBI' ? 'border-obi-orange/20 bg-obi-orange/5' : 'border-gray-100 bg-gray-50'}`} data-id="element-388">
                        <div className="flex items-center gap-2 min-w-0" data-id="element-389">
                          <img src={competitorLogos[store.type]} alt={store.type} className="h-4 w-auto max-w-[60px] object-contain shrink-0" data-id="element-390" />
                          <span className={`text-sm truncate ${store.type === 'OBI' ? 'font-medium text-gray-900' : 'text-gray-600'}`} data-id="element-391">
                            {store.name}
                          </span>
                        </div>
                        <div className="flex items-center gap-3 shrink-0 ml-2" data-id="element-392">
                          <span className="text-xs text-gray-400" data-id="element-393">
                            {store.distance}
                          </span>
                          <div className="flex items-center gap-1" data-id="element-394">
                            <span className={`text-sm font-semibold ${store.type === 'OBI' ? 'text-obi-orange' : 'text-gray-600'}`} data-id="element-395">
                              {store.rating.toFixed(1)}
                            </span>
                            <Star size={11} className="text-yellow-400 fill-yellow-400" data-id="element-396" />
                          </div>
                        </div>
                      </motion.div>)}
                  </div>
                </div>
              </div>
            </motion.div>}
        </div>
      </Card>
    </div>;
}